import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../../hooks/useToast';
import { useSwapNarrationDialogue } from '../../../features/scenarios/hooks';
import type {
  NarrationDialogueSwapErrorBody,
  NarrationDialogueSwapOptions,
  NarrationDialogueSwapScope,
  NarrationDialogueSwapSpeaker,
} from '../../../features/scenarios/types';
import { getResponseErrorMessage } from '../../../utils/getErrorMessage';
import type { MentionActorSource } from '../state/mentionUtils';
import { MaterialMentionPopup } from './MaterialMentionPopup';
import {
  CUSTOM_SPEAKER_VALUE,
  DIALOGUE_LINE_LABEL_MAX_CHARS,
  NARRATION_SPEAKER_VALUE,
  NO_SPEAKER_VALUE,
  buildDialogueSpeakerCandidates,
  buildDialogueSpeakerOptions,
  ensureSpeakerOptionIncludesCurrent,
  resolveDialogueSpeakerChip,
  resolveDialogueSpeakerSelection,
  speakerToSelectValue,
  type SpeakerLineSpeaker,
} from './dialogueLines';
import { useNarrationDialogueSwapPreview } from './hooks/useNarrationDialogueSwapPreview';
import { mergeSwapResultIntoScenario } from './narrationDialogueSwap';
import { DialogueSpeakerChip } from './sceneItem/DialogueSpeakerChip';
import { useDialogueSpeakerDropdown } from './sceneItem/useDialogueSpeakerDropdown';
import dialogueLineStyles from './sceneItem/DialogueLines.module.css';
import { buildDialogueSpeakerExtraOptions, scenarioCharacters } from './telopLines';
import type { Scenario, ScenarioResponse } from './types';

const EMPTY_ACTORS: MentionActorSource[] = [];
const MUTED = 'rgba(245, 241, 230, 0.6)';
const DANGER = 'rgba(248, 113, 113, 0.9)';
const NOTICE = '生成済みの動画・ナレーション音声は作り直しが必要になります。セリフをナレーションにした行の話者割り当ては解除され、ナレーターの声で読まれます。';
const SPEAKER_MODE_NOTICE = 'この操作は元に戻せません（入れ替え直しても元の配置には戻りません）';
const STALE_MESSAGE = '他の編集と競合しました。内容を確認し直してください';
const PICKER_ROW_ID = 'narration-speaker';

const EMPTY_SPEAKER: SpeakerLineSpeaker = { label: '', actor_id: null, actor_kind: null };

interface NarrationDialogueSwapModalProps {
  isOpen: boolean;
  onClose: () => void;
  scenario: Scenario;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
  /** 話者チップのバッジ・顔写真用 (SceneHandlersContextのmentionActorSources)。省略時は名前のみ */
  actors?: MentionActorSource[];
}

function sceneLabel(order: number | null | undefined): string {
  return `シーン${order ?? '?'}`;
}

/** 設問1の話者1件の表示 (俳優はチップ+名前・登場人物 / 自由ラベルは名前・未設定は「話者未設定」)。 */
function SwapSpeakerLabel({ speaker, actors }: { speaker: NarrationDialogueSwapSpeaker; actors: MentionActorSource[] }) {
  if (speaker.kind === 'unassigned') return <span>話者未設定</span>;
  const chip = speaker.kind === 'actor' && speaker.actor_id && speaker.actor_kind
    ? resolveDialogueSpeakerChip({ label: speaker.label, actor_id: speaker.actor_id, actor_kind: speaker.actor_kind }, actors)
    : null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {chip && <DialogueSpeakerChip badge={chip.badge} thumbnailUrl={chip.thumbnailUrl} name={chip.name} />}
      <span>{speaker.label || '話者未設定'}</span>
    </span>
  );
}

/**
 * シナリオ三点メニュー「セリフとナレーションを入れ替える」のモーダル
 * (docs/features/scenario-narration-dialogue-swap-plan.md §3.9)。
 *
 * 開くと既定の条件 (すべてのセリフ・話者未指定) でdry_runし、設問 (ナレーションにするセリフの範囲 / セリフにする
 * ナレーションの話者) と対象数・注意を見せる。下段の表示と実行に送る値は、確認済みプレビュー
 * (useNarrationDialogueSwapPreview) だけから作る。409 CONFIRMATION_STALEは閉じずに確認し直す。
 */
export function NarrationDialogueSwapModal({
  isOpen,
  onClose,
  scenario,
  setLastResult,
  actors = EMPTY_ACTORS,
}: NarrationDialogueSwapModalProps) {
  const toast = useToast();
  const swapMutation = useSwapNarrationDialogue();
  const [scope, setScope] = useState<NarrationDialogueSwapScope>('all');
  const [dialogueSpeakerId, setDialogueSpeakerId] = useState<string | null>(null);
  const [narrationSpeaker, setNarrationSpeaker] = useState<SpeakerLineSpeaker | null>(null);
  const [executing, setExecuting] = useState(false);
  // 話者の集計と「話者の指定が必要か」は選択条件に依らないので、条件の変更でpreviewが消えている間も設問を出し続ける
  const [knownSpeakers, setKnownSpeakers] = useState<NarrationDialogueSwapSpeaker[]>([]);
  const [knownRequiresSpeaker, setKnownRequiresSpeaker] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setScope('all');
    setDialogueSpeakerId(null);
    setNarrationSpeaker(null);
    setExecuting(false);
    setKnownSpeakers([]);
    setKnownRequiresSpeaker(false);
  }, [isOpen, scenario.id]);

  const options = useMemo<NarrationDialogueSwapOptions>(() => ({
    dialogue_scope: scope,
    dialogue_speaker_id: scope === 'speaker' ? dialogueSpeakerId : null,
    narration_speaker: narrationSpeaker
      ? {
        label: narrationSpeaker.label,
        actor_id: narrationSpeaker.actor_id,
        actor_kind: narrationSpeaker.actor_kind,
        character_key: narrationSpeaker.character_key ?? null,
      }
      : null,
  }), [scope, dialogueSpeakerId, narrationSpeaker]);

  const { preview, status, error, canExecute, executePayload, refresh } = useNarrationDialogueSwapPreview({
    scenarioId: scenario.id,
    open: isOpen,
    options,
  });

  useEffect(() => {
    if (!preview) return;
    setKnownSpeakers(preview.speakers ?? []);
    setKnownRequiresSpeaker(!!preview.requires_narration_speaker);
  }, [preview]);

  // 設問2の話者ピッカー: キャスト俳優 + 画面に出る登場人物 + 自由入力 (声だけの登場人物・ナレーター・話者なしは除く)
  const speakerOptionsBase = useMemo(() => buildDialogueSpeakerOptions(scenario), [scenario]);
  const extraOptions = useMemo(
    () => buildDialogueSpeakerExtraOptions(
      scenarioCharacters(scenario).filter((c) => c.appears_on_screen !== false),
    ).filter((o) => o.value !== NARRATION_SPEAKER_VALUE && o.value !== NO_SPEAKER_VALUE),
    [scenario],
  );
  const pickerSpeaker = narrationSpeaker ?? EMPTY_SPEAKER;
  const speakerOptions = ensureSpeakerOptionIncludesCurrent(speakerOptionsBase, pickerSpeaker);
  const candidates = buildDialogueSpeakerCandidates(speakerOptions, actors, extraOptions);
  const speakerDropdown = useDialogueSpeakerDropdown();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const selectValue = narrationSpeaker ? speakerToSelectValue(narrationSpeaker) : '';
  const isCustomSpeaker = !!narrationSpeaker && selectValue === CUSTOM_SPEAKER_VALUE;
  const applySpeakerSelection = (value: string) => {
    const base = narrationSpeaker ?? EMPTY_SPEAKER;
    const patch = resolveDialogueSpeakerSelection(value, base.label, speakerOptions, extraOptions);
    const next: SpeakerLineSpeaker = { ...base, ...patch };
    delete next.special;
    delete next.kind;
    setNarrationSpeaker(next);
    speakerDropdown.dropdown.close();
  };
  const pickerChip = narrationSpeaker ? resolveDialogueSpeakerChip(narrationSpeaker, actors) : null;

  if (!isOpen) return null;

  const busy = executing || swapMutation.isPending;
  const showScopeQuestion = knownSpeakers.length >= 2;
  const showSpeakerQuestion = preview ? preview.requires_narration_speaker : knownRequiresSpeaker;
  const checking = status === 'pending' || status === 'loading';

  const close = () => {
    if (busy) return;
    speakerDropdown.dropdown.close();
    onClose();
  };

  const execute = async () => {
    if (!executePayload || busy) return;
    setExecuting(true);
    try {
      const result = await swapMutation.mutateAsync({
        scenarioId: scenario.id,
        options: executePayload.options,
        confirmation: executePayload.confirmation,
      });
      setLastResult((prev) => (prev ? {
        ...prev,
        scenarios: prev.scenarios.map((s) => (s.id === scenario.id ? mergeSwapResultIntoScenario(s, result.scenes ?? []) : s)),
      } : prev));
      toast.success('セリフとナレーションを入れ替えました');
      setExecuting(false);
      onClose();
    } catch (e) {
      setExecuting(false);
      const resp = (e as { response?: { status?: number; data?: NarrationDialogueSwapErrorBody } }).response;
      if (resp?.status === 409 && resp.data?.error_code === 'CONFIRMATION_STALE') {
        toast.error(STALE_MESSAGE);
        refresh();
        return;
      }
      toast.error(getResponseErrorMessage(e) || 'セリフとナレーションの入れ替えに失敗しました');
    }
  };

  const changeScope = (next: NarrationDialogueSwapScope) => {
    setScope(next);
    if (next === 'speaker' && !dialogueSpeakerId && knownSpeakers[0]) setDialogueSpeakerId(knownSpeakers[0].speaker_id);
  };

  // ---- 下段 (確認済みプレビューだけから描く) ----
  const targetScenes = preview ? preview.scenes.filter((s) => s.moved_to_narration > 0 || s.moved_to_dialogue > 0) : [];
  const audioClearedCount = preview ? preview.scenes.filter((s) => s.audio_cleared).length : 0;
  const warningScenes = (code: string) => (preview ? preview.scenes.filter((s) => s.warnings.includes(code)) : []);
  const voiceChanged = preview ? preview.scenes.filter((s) => s.warnings.includes('effective_voice_source_changed') || s.voice_source_changes.length > 0) : [];
  const mixed = warningScenes('mixed_voice_sources');
  const excludedKept = warningScenes('excluded_lines_kept');
  const legacyKept = warningScenes('legacy_scene_dialogue_kept');

  return createPortal(
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-label="セリフとナレーションを入れ替える"
        data-testid="narration-dialogue-swap-modal"
        style={{ maxWidth: 560, width: 'min(560px, calc(100vw - 32px))' }}
      >
        <div className="modal-header">
          <h3 className="modal-title">セリフとナレーションを入れ替える</h3>
          <button type="button" className="modal-close-button" onClick={close} aria-label="閉じる" disabled={busy}>
            ✕
          </button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13 }}>
          <span style={{ color: MUTED }}>
            各シーンのセリフをナレーション欄へ、ナレーションをセリフ欄へ移します。
          </span>

          {showScopeQuestion && (
            <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="swap-scope-question">
              <legend style={{ fontWeight: 600, marginBottom: 4 }}>ナレーションにするセリフ</legend>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" name="swap-scope" checked={scope === 'all'} onChange={() => changeScope('all')} disabled={busy} />
                すべてのセリフ
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" name="swap-scope" checked={scope === 'speaker'} onChange={() => changeScope('speaker')} disabled={busy} />
                この話者の行だけ
              </label>
              {scope === 'speaker' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 22 }} data-testid="swap-scope-speakers">
                  {knownSpeakers.map((sp) => (
                    <label key={sp.speaker_id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="radio"
                        name="swap-scope-speaker"
                        value={sp.speaker_id}
                        checked={dialogueSpeakerId === sp.speaker_id}
                        onChange={() => setDialogueSpeakerId(sp.speaker_id)}
                        disabled={busy}
                      />
                      <SwapSpeakerLabel speaker={sp} actors={actors} />
                      <span style={{ color: MUTED, fontSize: 12 }}>{`${sp.line_count}行・${sp.scene_count}シーン`}</span>
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
          )}

          {showSpeakerQuestion && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="swap-narration-speaker-question">
              <span style={{ fontWeight: 600 }}>セリフにするナレーションの話者</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  ref={triggerRef}
                  role="button"
                  tabIndex={0}
                  className={dialogueLineStyles.speakerTrigger}
                  data-testid="swap-narration-speaker-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={speakerDropdown.isOpenFor(PICKER_ROW_ID)}
                  aria-label="話者"
                  onClick={(e) => { if (!busy) speakerDropdown.toggle(PICKER_ROW_ID, e.currentTarget, candidates, selectValue); }}
                  onKeyDown={(e) => speakerDropdown.handleTriggerKeyDown(
                    e,
                    PICKER_ROW_ID,
                    e.currentTarget,
                    candidates,
                    selectValue,
                    (candidate) => applySpeakerSelection(candidate.target.id),
                  )}
                >
                  {pickerChip ? (
                    <DialogueSpeakerChip badge={pickerChip.badge} thumbnailUrl={pickerChip.thumbnailUrl} name={pickerChip.name} size="lg" />
                  ) : narrationSpeaker?.label ? (
                    <DialogueSpeakerChip badge="" thumbnailUrl={null} name={narrationSpeaker.label} size="lg" />
                  ) : (
                    <span className={`${dialogueLineStyles.speakerTriggerLabel} ${dialogueLineStyles.speakerTriggerPlaceholder}`}>
                      {isCustomSpeaker ? '自由入力' : '話者を選択'}
                    </span>
                  )}
                  <svg viewBox="0 0 10 6" width="8" height="5" aria-hidden="true" className={dialogueLineStyles.speakerTriggerChevron}>
                    <path d="M1 1 L5 5 L9 1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </div>
                {narrationSpeaker && !isCustomSpeaker && <span>{narrationSpeaker.label}</span>}
                {isCustomSpeaker && (
                  <input
                    className={dialogueLineStyles.speakerLabelInput}
                    type="text"
                    value={narrationSpeaker?.label ?? ''}
                    maxLength={DIALOGUE_LINE_LABEL_MAX_CHARS}
                    placeholder="話者名"
                    data-testid="swap-narration-speaker-label"
                    onChange={(e) => setNarrationSpeaker((prev) => ({ ...(prev ?? EMPTY_SPEAKER), label: e.target.value }))}
                    disabled={busy}
                  />
                )}
              </div>
              {!options.narration_speaker?.label && !options.narration_speaker?.actor_id && !options.narration_speaker?.character_key && (
                <span style={{ color: MUTED, fontSize: 12 }}>話者を選ぶと実行できます</span>
              )}
              <MaterialMentionPopup
                ref={speakerDropdown.dropdown.menuRef}
                candidates={speakerDropdown.isOpenFor(PICKER_ROW_ID) ? candidates : []}
                activeIndex={speakerDropdown.activeIndex}
                position={speakerDropdown.dropdown.pos}
                onHoverIndex={speakerDropdown.setActiveIndex}
                onSelect={(candidate) => applySpeakerSelection(candidate.target.id)}
              />
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, borderTop: '1px solid rgba(245, 241, 230, 0.1)', paddingTop: 10 }} data-testid="swap-summary">
            {checking && <span style={{ color: MUTED }} data-testid="swap-checking">確認中…</span>}
            {status === 'error' && (
              <span style={{ color: DANGER }} data-testid="swap-error">
                {getResponseErrorMessage(error) || '内容の確認に失敗しました'}
              </span>
            )}
            {preview && !checking && (
              <>
                {!preview.applicable ? (
                  <span style={{ color: MUTED }} data-testid="swap-not-applicable">入れ替える本文がありません</span>
                ) : (
                  <span data-testid="swap-target-count">{`対象: ${targetScenes.length}シーン`}</span>
                )}
                {preview.skipped.length > 0 && (
                  <span style={{ color: MUTED }}>{`対象外: ${preview.skipped.length}シーン（タイトルカード・エンドカード・本文のないシーン）`}</span>
                )}
                {audioClearedCount > 0 && (
                  <span style={{ color: 'rgba(255, 220, 160, 0.95)' }}>
                    {`${audioClearedCount}シーンのナレーション音声の選択を解除します`}
                  </span>
                )}
                {voiceChanged.map((s) => (
                  <span key={`vc-${s.scene_id}`} style={{ color: 'rgba(255, 220, 160, 0.95)' }}>
                    {`${sceneLabel(s.order)}: 残るセリフ行の読み上げ方法が変わります`}
                  </span>
                ))}
                {mixed.map((s) => (
                  <span key={`mx-${s.scene_id}`} style={{ color: 'rgba(255, 220, 160, 0.95)' }}>
                    {`${sceneLabel(s.order)}: 動画AIの発話とTTSが混在するため、声の生成前に読み手を揃えてください`}
                  </span>
                ))}
                {excludedKept.map((s) => (
                  <span key={`ex-${s.scene_id}`} style={{ color: MUTED }}>
                    {`${sceneLabel(s.order)}: 声の生成から外した行はセリフ欄に残します`}
                  </span>
                ))}
                {legacyKept.map((s) => (
                  <span key={`lg-${s.scene_id}`} style={{ color: MUTED }}>
                    {`${sceneLabel(s.order)}: 話者の無い旧形式のセリフはそのまま残します`}
                  </span>
                ))}
              </>
            )}
            <span style={{ color: MUTED, fontSize: 12, marginTop: 4 }}>{NOTICE}</span>
            {scope === 'speaker' && showScopeQuestion && (
              <span style={{ color: MUTED, fontSize: 12 }} data-testid="swap-speaker-mode-notice">{SPEAKER_MODE_NOTICE}</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px' }}>
          <button type="button" className="btn-cancel" onClick={close} disabled={busy}>
            キャンセル
          </button>
          <button
            type="button"
            className="btn-modal"
            onClick={() => void execute()}
            disabled={!canExecute || busy}
            data-testid="swap-execute"
          >
            {busy ? '入れ替え中…' : '入れ替える'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
